import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, User, ArrowRight, Ghost } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BLOG_POSTS } from '@/data/blogPosts';
import SEO from '@/components/ui-custom/SEO';

interface Post {
  title: string;
  description: string;
  category: string;
  image: string;
  date: string;
  author: string;
  slug: string;
}

const CATEGORIES = ['All', 'Plumbing Tips', 'Electrical Safety', 'Local Events', 'Guides'];

const Blog = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const posts: Post[] = BLOG_POSTS;

  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      const matchesSearch =
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, activeCategory]);

  return (
    <>
      <SEO
        title="Expert Plumbing & Electrical Blog | Dothan AL"
        description="Read the latest home maintenance guides, safety tips, and local Wiregrass events from Ard's Plumbing & Electric."
        keywords="plumbing tips Dothan AL, electrical safety Alabama, home maintenance Wiregrass, plumbing guides, electrician tips"
        canonical="/blog"
        additionalSchema={{ '@context': 'https://schema.org', '@type': 'BreadcrumbList', 'itemListElement': [{ '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' }, { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': 'https://ardsdothan.com/blog' }] }}
      />

      <section className="min-h-screen bg-brand-blue pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header & Search */}
          <div className="bg-white rounded-[28px] p-8 lg:p-12 shadow-2xl mb-12">
            <h1 className="font-heading font-black text-4xl lg:text-5xl text-brand-black mb-8">
              THE <span className="text-brand-coral">WIREGRASS</span> BLOG
            </h1>

            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search guides, tips, or questions..."
                  className="w-full pl-12 pr-4 py-4 rounded-full border-2 border-gray-100 focus:border-brand-blue outline-none transition-all"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button className="bg-brand-navy hover:bg-brand-blue text-white rounded-full px-8 py-4 h-auto font-bold shadow-md">
                Search
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 mt-8">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${activeCategory === cat
                      ? 'bg-brand-coral text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Results Area */}
          <div className="space-y-8">
            {filteredPosts.length > 0 ? (
              filteredPosts.map((post) => (
                <article key={post.slug} className="bg-white rounded-[28px] overflow-hidden shadow-xl flex flex-col md:flex-row group hover:shadow-2xl transition-all cursor-pointer">

                  <Link to={`/blog/${post.slug}`} className="md:w-1/3 h-64 md:h-auto relative overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </Link>

                  <div className="md:w-2/3 p-8 lg:p-10 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-4 text-sm text-brand-coral font-bold mb-4 uppercase tracking-widest">
                        {post.category}
                      </div>

                      <Link to={`/blog/${post.slug}`} className="inline-block">
                        <h2 className="font-heading font-black text-2xl lg:text-3xl text-brand-black mb-4 group-hover:text-brand-blue transition-colors leading-tight">
                          {post.title}
                        </h2>
                      </Link>

                      <p className="text-gray-600 mb-8 line-clamp-2">
                        {post.description}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-gray-100">
                      <div className="flex items-center gap-6 text-sm text-gray-500">
                        <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> {post.date}</span>
                        <span className="flex items-center gap-2"><User className="w-4 h-4" /> {post.author}</span>
                      </div>

                      <Button variant="ghost" asChild className="text-brand-navy font-bold gap-2 group/btn p-0 hover:bg-transparent">
                        <Link to={`/blog/${post.slug}`}>
                          Read More <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </article>
              ))
            ) : (
              <div className="bg-white/10 backdrop-blur-md border-2 border-dashed border-white/20 rounded-[28px] p-20 text-center text-white">
                <Ghost className="w-16 h-16 mx-auto mb-6 opacity-50" />
                <h3 className="text-2xl font-bold mb-2">No content found</h3>
                <p className="opacity-70">We're currently drafting new guides for the Wiregrass. Check back soon!</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Blog;